import { CloseIcon, HamburgerIcon } from '@chakra-ui/icons';
import {
    Avatar,
    Box,
    Button,
    Collapse,
    Flex,
    HStack,
    IconButton,
    Image,
    Link,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Stack,
    useDisclosure
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Link as ReactLink } from 'react-router-dom';
import { auth, logoutHelper } from '../firebase';
import { useMeQuery } from '../generated/graphql';
import logo from '../resources/glyph-purple.svg';

const NavLink = ({
    children,
    link,
    reactLink
}: {
    children: ReactNode;
    link: string;
    reactLink: boolean;
}) => (
    <Link
        as={reactLink ? ReactLink : undefined}
        px={2}
        py={1}
        fontWeight={'400'}
        rounded={'md'}
        color={'midnight.400'}
        border="1px solid transparent"
        transition="border 200ms ease"
        _hover={{
            color: 'midnight.500',
            borderColor: 'midnight.50'
        }}
        _active={{
            bg: 'midnight.15'
        }}
        to={link}
        href={link}
    >
        {children}
    </Link>
);

function NavBar() {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [user] = useAuthState(auth);
    const { loading, error, data: userData } = useMeQuery();

    let profileLink: [string, string, boolean] = ['Profile', '', true];
    if (!loading) {
        if (userData?.me?.profile) {
            profileLink = ['Profile', `/profile/${userData?.me?.profile?.slug}`, true];
        } 
        else {
            profileLink = ['Create Profile', '/createProfile', true];
        }
    }
    const Links: [string, string, boolean][] = [
        ['Resources', 'https://www.pathmatch.com/paths', false],
        ['Job Board', '/job-board', true],
        profileLink,
        ['Dashboard', '/dashboard', true]
    ];

    return (
        <>
            <Box
                bg="white"
                px={4}
                width="100%"
                borderBottom="1px solid"
                borderBottomColor={'midnight.50'}
            >
                <Box maxWidth="container.xl" marginRight="auto" marginLeft="auto">
                    <Flex height={20} alignItems={'center'} justifyContent={'space-between'}>
                        <IconButton
                            size={'md'}
                            bg="white"
                            icon={
                                isOpen ? (
                                    <CloseIcon w="30px" />
                                ) : (
                                    <HamburgerIcon w="30px" h="30px" />
                                )
                            }
                            aria-label={'Open Menu'}
                            display={{ md: 'none' }}
                            onClick={isOpen ? onClose : onOpen}
                        />
                        <HStack spacing={8} alignItems={'center'}>
                            <Image h={8} src={logo} alt="Logo" />
                        </HStack>
                        <Flex alignItems={'center'}>
                            <HStack
                                as={'nav'}
                                marginX={3}
                                spacing={4}
                                display={{ base: 'none', md: 'flex' }}
                            >
                                {Links.map(([name, link, reactLink]) => (
                                    <NavLink key={name} link={link} reactLink={reactLink}>
                                        {name}
                                    </NavLink>
                                ))}
                            </HStack>
                            <Menu>
                                <MenuButton
                                    as={Button}
                                    variant={'link'}
                                    cursor={'pointer'}
                                    minW={0}
                                >
                                    <Avatar size={'sm'} src={user?.photoURL ?? ''} />
                                </MenuButton>
                                <MenuList>
                                    <MenuItem onClick={logoutHelper}>Sign Out</MenuItem>
                                </MenuList>
                            </Menu>
                        </Flex>
                    </Flex>

                    <Collapse in={isOpen} animateOpacity>
                        <Box pb={4} display={{ md: 'none' }}>
                            <Stack as={'nav'} spacing={4}>
                                {Links.map(([name, link, reactLink]) => (
                                    <NavLink key={name} link={link} reactLink={reactLink}>
                                        {name}
                                    </NavLink>
                                ))}
                            </Stack>
                        </Box>
                    </Collapse>
                </Box>
            </Box>
        </>
    );
}

export default NavBar;
